import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { SubmissionsService } from 'src/app/services/submissions.service';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'src/app/services/language.service';
import { Store } from '@ngrx/store';
import * as submissionsActions from '../state/submissions/action/submissions.actions';
import { getSubmissionsStateDetails } from '../state/submissions/selector/submissions.selectors';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ReportService } from 'src/app/sharedModule/report-v2/services/report.service';
import { ExternalSessionService, SessionResponse } from 'src/app/services/external-session.service';

interface Submission {
  id: string;
  name: string;
  submissionTime: Date;
  riskLevel: 'high' | 'medium' | 'low';
}

@Component({
  selector: 'app-precheck-external',
  templateUrl: './precheck-external.component.html',
  styleUrls: ['./precheck-external.component.scss']
})
export class PrecheckExternalComponent implements OnInit, OnDestroy {
  submissions: any[] = [];
  totalSubmissions = 0;
  pageSize = 5;
  isDragging = false;
  fileToUpload: File | null = null;

  page = 1;
  assignment: any;
  assignmentsSubmissions: any;
  submissionsLoaded = Promise.resolve(false);
  getSubmissionsStateDetails$: any;
  showAssignmentSubmission = true;
  pre_check = true;
  user;
  paginatedSubmissions: any[] = [];

  private TOKEN;
  private queryParamSubscription: Subscription;
  private sessionId: string | null = null;
  sessionValidated = false;
  sessionData: SessionResponse | null = null;

  // Add a map to store submission scores
  submissionScores: Map<string, number> = new Map();
  loadingScores: Map<string, boolean> = new Map();

  // Add a new property to track the maximum uploads and remaining uploads
  maxPrecheckUploads = 10; // Default value
  remainingUploads = 10;
  isStudent = false;

  constructor(
    private submissionsService: SubmissionsService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private router: Router,
    private translate: TranslateService,
    private languageService: LanguageService,
    private store: Store,
    private route: ActivatedRoute,
    private http: HttpClient,
    private reportService: ReportService,
    private externalSessionService: ExternalSessionService
  ) {}

  ngOnDestroy(): void {
    if (this.getSubmissionsStateDetails$) {
      this.getSubmissionsStateDetails$.unsubscribe();
    }
    if (this.queryParamSubscription) {
      this.queryParamSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.spinner.show();

    // Subscribe to route parameters for session ID
    this.route.params.subscribe(params => {
      if (params['sessionId']) {
        this.sessionId = params['sessionId'];
        this.verifySession(this.sessionId);
      } else {
        // Check URL segments for session route pattern
        this.route.url.subscribe(segments => {
          if (segments.length >= 2 && segments[0].path === 'session') {
            this.sessionId = segments[1].path;
            this.verifySession(this.sessionId);
          } else {
            // Fallback to regular token if no session available
            this.TOKEN = JSON.parse(localStorage.getItem('ACCESS_TOKEN'));

            if (!this.TOKEN) {
              this.toastr.error('Authentication error: No valid token or session found');
              this.spinner.hide();
              return;
            }

            this.sessionValidated = true;
            this.initializeComponent();
          }
        });
      }
    });
  }

  /**
   * Verify the session with the backend
   * @param sessionId The session ID to verify
   */
  private verifySession(sessionId: string): void {
    this.spinner.show();
    this.externalSessionService.verifySession(sessionId, true)
      .subscribe(
        (response: SessionResponse) => {
          if (response.valid) {
            this.sessionValidated = true;
            this.sessionData = response;
            this.TOKEN = response.token;
            this.user = response.user;

            // Store token as JSON string consistently

            localStorage.setItem('ACCESS_TOKEN', JSON.stringify(this.TOKEN));
            localStorage.setItem('user', JSON.stringify(this.user));

            // Initialize the component after session validation
            this.initializeComponent();
          } else {
            console.error('Invalid session:', response);
            this.toastr.error('Authentication error: Invalid session');
            this.spinner.hide();
          }
        },
        (error) => {
          console.error('Error verifying session:', error);
          this.toastr.error('Authentication error: ' + (error.error?.message || 'Failed to verify session'));
          this.spinner.hide();
        }
      );
  }

  /**
   * Initialize the component after authentication
   */
  private initializeComponent(): void {
    // Set language
    this.languageService.getUserLanguage$.subscribe((language) => {
      if (language == null) {
        language = localStorage.getItem('websiteLanguage') || 'en';
      }
      this.translate.use(language);
    });

    const user = JSON.parse(localStorage.getItem('user'));

    this.isStudent = user?.roleId == 5||user?.roleId == 9;

    if (user?.Institution?.settings) {
      this.maxPrecheckUploads = user.Institution.settings.maxPrecheckUploads || 10;
    }

    // Subscribe to query parameter changes for refresh
    this.queryParamSubscription = this.route.queryParams.subscribe(params => {
      if (params['refresh']) {
        // Refresh submissions when the refresh parameter changes
        this.loadSubmissions(true);

        // Remove the refresh parameter from URL after loading
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { refresh: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      } else {
        // Initial load without refresh parameter
        this.loadSubmissions(false);
      }
    });
  }

  /**
   * Method to view the report for a submission
   * @param id Submission ID
   */
  viewReport(id: string): void {
    this.spinner.show();

    // Get user from state or localStorage
    const user = this.user || JSON.parse(localStorage.getItem('user'));

    if (!user || !user.id) {
      console.error('Invalid user data:', user);
      this.toastr.error('Authentication error: Invalid user data');
      this.spinner.hide();
      return;
    }

    // Add marketplaceId if available (for marketplace users)
    const marketplaceId = user.Institution?.name?.startsWith('inspera-')
      ? user.Institution.name.substring(8) // Extract marketplaceId from 'inspera-{marketplaceId}'
      : undefined;

    this.externalSessionService.createExternalSession(
      user.id,
      3600,
      false,
      true,
      id,
      marketplaceId
    ).subscribe(
      (sessionUrl: string) => {
        window.open(sessionUrl, '_blank');
        this.spinner.hide();
      },
      (error) => {
        console.error('Error creating external session:', error);
        this.toastr.error('Failed to create session for report. Please try again.');
        this.spinner.hide();
      }
    );
  }

  /**
   * Handle page size change
   */
  onPageSizeChange(): void {
    // Convert pageSize to number to ensure proper calculations
    this.pageSize = Number(this.pageSize);
    this.page = 1;
    this.applyPagination();
    this.loadSubmissions();
  }

  /**
   * Change page in pagination
   * @param event Page number
   */
  changePage(event): void {
    this.page = event;
    this.applyPagination();
    this.loadSubmissions();
  }

  /**
   * Apply client-side pagination if server doesn't support it
   */
  private applyPagination(): void {
    const user = JSON.parse(localStorage.getItem('user'));
    const roleId = user?.roleId;
    const isProfessor = roleId === 10;

    if (!this.assignmentsSubmissions) return;

    // Ensure pageSize is a number for calculations
    const pageSize = Number(this.pageSize);

    // Handle professor submissions
    if (isProfessor && this.assignmentsSubmissions.getProfessorSubmissions) {
      // If we have stored all submissions from a previous pagination, use that
      if (this.assignmentsSubmissions._allSubmissions) {
        const allSubmissions = this.assignmentsSubmissions._allSubmissions;
        const startIndex = (this.page - 1) * pageSize;
        const endIndex = Math.min(startIndex + pageSize, this.totalSubmissions);

        this.paginatedSubmissions = allSubmissions.slice(startIndex, endIndex);

        // Update the current view with paginated submissions
        this.assignmentsSubmissions = {
          ...this.assignmentsSubmissions,
          getProfessorSubmissions: this.paginatedSubmissions,
          _allSubmissions: allSubmissions
        };
      }
      // First time pagination - API returned all submissions at once
      else if (this.assignmentsSubmissions.getProfessorSubmissions.length > pageSize) {
        // Store the full list for future pagination
        const allSubmissions = [...this.assignmentsSubmissions.getProfessorSubmissions];

        const startIndex = (this.page - 1) * pageSize;
        const endIndex = Math.min(startIndex + pageSize, this.totalSubmissions);

        this.paginatedSubmissions = allSubmissions.slice(startIndex, endIndex);

        // Replace the original submissions with our paginated version
        this.assignmentsSubmissions = {
          ...this.assignmentsSubmissions,
          getProfessorSubmissions: this.paginatedSubmissions,
          _allSubmissions: allSubmissions
        };
      }
    }
    // Handle student submissions
    else if (this.assignmentsSubmissions.studentSubmissions) {
      // If we have stored all submissions from a previous pagination, use that
      if (this.assignmentsSubmissions._allSubmissions) {
        const allSubmissions = this.assignmentsSubmissions._allSubmissions;
        const startIndex = (this.page - 1) * pageSize;
        const endIndex = Math.min(startIndex + pageSize, this.totalSubmissions);

        this.paginatedSubmissions = allSubmissions.slice(startIndex, endIndex);

        // Update the current view with paginated submissions
        this.assignmentsSubmissions = {
          ...this.assignmentsSubmissions,
          studentSubmissions: this.paginatedSubmissions,
          _allSubmissions: allSubmissions
        };
      }
      // First time pagination - API returned all submissions at once
      else if (this.assignmentsSubmissions.studentSubmissions.length > pageSize) {
        // Store the full list for future pagination
        const allSubmissions = [...this.assignmentsSubmissions.studentSubmissions];

        const startIndex = (this.page - 1) * pageSize;
        const endIndex = Math.min(startIndex + pageSize, this.totalSubmissions);

        this.paginatedSubmissions = allSubmissions.slice(startIndex, endIndex);

        // Replace the original submissions with our paginated version
        this.assignmentsSubmissions = {
          ...this.assignmentsSubmissions,
          studentSubmissions: this.paginatedSubmissions,
          _allSubmissions: allSubmissions
        };
      }
    }
  }

  /**
   * Load submissions with current filters
   * @param forceRefresh Whether to force a refresh from the server
   */
  private loadSubmissions(forceRefresh: boolean = false): void {
    this.spinner.show();

    // If we have cached data and don't need to force refresh, use client-side pagination
    if (!forceRefresh && this.assignmentsSubmissions && this.assignmentsSubmissions._allSubmissions) {
      this.applyPagination();
      this.spinner.hide();
      return;
    }

    // Clear any cached data if forcing refresh
    if (forceRefresh) {
      this.assignmentsSubmissions = null;
      this.submissionScores.clear();
    }

    // Make sure we have a valid token - using JSON.parse consistently
    if (!this.TOKEN) {
      try {
        // Retrieve and parse the token
        const storedToken = localStorage.getItem('ACCESS_TOKEN');
        this.TOKEN = storedToken ? JSON.parse(storedToken) : null;
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }

    if (!this.TOKEN) {
      this.toastr.error('Authentication error: No valid token found');
      this.spinner.hide();
      return;
    }

    const user = JSON.parse(localStorage.getItem('user'));
    const roleId = user?.roleId;

    if (roleId == 10) {
      this.submissionsService.getProfessorSubmissions(user.id, '-1', null, null).subscribe(
        (response: any) => {
          console.log('response Educator loadSubmissions:', response);
          this.assignmentsSubmissions = response;

          // Handle total count for professor submissions
          if (response.totalSubmissions !== undefined) {
            this.totalSubmissions = response.totalSubmissions;
          } else if (response.countProfessorSubmissions !== undefined) {
            this.totalSubmissions = response.countProfessorSubmissions;
          } else {
            this.totalSubmissions = response.getProfessorSubmissions?.length || 0;
          }

          // Set the totalSubmissions property consistently
          this.assignmentsSubmissions.totalSubmissions = this.totalSubmissions;

          this.applyPagination();
          this.loadSubmissionScores();
          this.submissionsLoaded = Promise.resolve(true);
          this.spinner.hide();

          // After setting this.totalSubmissions, add:
          this.calculateRemainingUploads();
        },
        (error) => {
          console.error('API error loading submissions:', error);
          this.spinner.hide();
          this.toastr.error('Failed to load submissions. Please try again.');
        }
      );
    } else {
      this.submissionsService.getAssignmentStudentSubmissions(this.page, null, true).subscribe(
        (response: any) => {
          this.assignmentsSubmissions = response;

          // Handle total count for student submissions
          if (response.totalSubmissions !== undefined) {
            this.totalSubmissions = response.totalSubmissions;
          } else if (response.totalStudentSubmissions !== undefined) {
            this.totalSubmissions = response.totalStudentSubmissions;
          } else {
            this.totalSubmissions = response.studentSubmissions?.length || 0;
          }

          // Set the totalSubmissions property consistently
          this.assignmentsSubmissions.totalSubmissions = this.totalSubmissions;

          this.applyPagination();
          this.loadSubmissionScores();
          this.submissionsLoaded = Promise.resolve(true);
          this.spinner.hide();

          // After setting this.totalSubmissions, add:
          this.calculateRemainingUploads();
        },
        (error) => {
          console.error('API error loading submissions:', error);
          this.spinner.hide();
          this.toastr.error('Failed to load submissions. Please try again.');
        }
      )
    }
  }

  /**
   * Load scores for all visible submissions
   */
  private loadSubmissionScores(): void {
    if (!this.assignmentsSubmissions) {
      return;
    }

    const user = JSON.parse(localStorage.getItem('user'));
    const roleId = user?.roleId;
    const isProfessor = roleId === 10;

    // Get the appropriate submissions array based on user role
    const submissions = isProfessor
      ? this.assignmentsSubmissions.getProfessorSubmissions
      : this.assignmentsSubmissions.studentSubmissions;

    // Fetch scores for all visible submissions
    if (submissions?.length) {
      submissions.forEach(submission => {
        if (!this.submissionScores.has(submission.id) && !this.loadingScores.get(submission.id)) {
          this.loadSubmissionScore(submission.id);
        }
      });
    }
  }

  /**
   * Load originality score for a single submission
   * @param submissionId Submission ID
   */
  private loadSubmissionScore(submissionId: string): void {
    // Mark as loading to prevent duplicate requests
    this.loadingScores.set(submissionId, true);

    this.reportService.submissionDetails(submissionId).subscribe(
      (response: any) => {
        this.submissionScores.set(submissionId, response?.originality || 0);
        this.loadingScores.set(submissionId, false);
      },
      (error) => {
        console.error(`Error fetching score for submission ${submissionId}:`, error);
        this.loadingScores.set(submissionId, false);
        // Set a default value on error
        this.submissionScores.set(submissionId, 0);
      }
    );
  }

  /**
   * Get the risk level based on originality score
   * @param score Originality score
   * @returns Risk level: 'high-risk', 'medium-risk', or 'low-risk'
   */
  getRiskLevel(score: number): string {
    return score ? score.toString().split(' ').join('-').toLowerCase() : 'no-score';
  }

  /**
   * Get submission score from the cache
   * @param submissionId Submission ID
   * @returns Cached score or null if not loaded yet
   */
  getSubmissionScore(submissionId: string): number | null {
    return this.submissionScores.has(submissionId)
      ? this.submissionScores.get(submissionId)
      : null;
  }

  /**
   * Check if a submission score is still loading
   * @param submissionId Submission ID
   * @returns True if the score is still loading
   */
  isScoreLoading(submissionId: string): boolean {
    return this.loadingScores.get(submissionId) === true;
  }

  /**
   * Handle keyboard navigation for the pagination controls
   * @param event KeyboardEvent
   */
  @HostListener('keydown', ['$event'])
  handleKeyboardNavigation(event: KeyboardEvent): void {
    // Handle keyboard navigation for pagination
    if (event.target && (event.target as HTMLElement).classList.contains('pagination-arrow')) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const button = event.target as HTMLButtonElement;
        if (!button.disabled) {
          if (button.classList.contains('prev')) {
            this.changePage(this.page - 1);
          } else if (button.classList.contains('next')) {
            this.changePage(this.page + 1);
          }
        }
      }
    }

    // Handle keyboard navigation for report buttons
    if (event.target && (event.target as HTMLElement).classList.contains('report-btn')) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const button = event.target as HTMLButtonElement;
        const submissionId = this.getSubmissionIdFromButton(button);
        if (submissionId) {
          this.viewReport(submissionId);
        }
      }
    }
  }

  /**
   * Get the submission ID from a report button element
   * @param button The report button element
   * @returns The submission ID or null if not found
   */
  private getSubmissionIdFromButton(button: HTMLButtonElement): string | null {
    // Find the parent row
    const row = button.closest('tr');
    if (!row) return null;

    // Get the index from the data-index attribute
    const index = row.getAttribute('data-index');
    if (index === null) return null;

    // Get the submission at that index from the active submissions array
    const submissions = this.getActiveSubmissions();
    const submission = submissions[parseInt(index, 10)];
    return submission ? submission.id : null;
  }

  /**
   * Refresh submissions data
   */
  refreshSubmissions(): void {
    // Clear scores cache to force reload
    this.submissionScores.clear();
    this.loadingScores.clear();

    // Show spinner
    this.spinner.show();

    // Force refresh of submissions
    this.loadSubmissions(true);

    // Show success message
    this.toastr.info('Refreshing submissions data...');
  }

  /**
   * Get the active submissions array based on user role
   * @returns The appropriate submissions array
   */
  getActiveSubmissions(): any[] {
    if (!this.assignmentsSubmissions) {
      return [];
    }

    const user = JSON.parse(localStorage.getItem('user'));
    const roleId = user?.roleId;
    const isProfessor = roleId === 10;

    return isProfessor
      ? this.assignmentsSubmissions.getProfessorSubmissions || []
      : this.assignmentsSubmissions.studentSubmissions || [];
  }

  /**
   * Check if any submissions are available
   */
  hasSubmissions(): boolean {
    const submissions = this.getActiveSubmissions();
    return submissions && submissions.length > 0;
  }

  // Add a method to calculate remaining uploads
  private calculateRemainingUploads(): void {
    if (this.totalSubmissions > 0) {
      this.remainingUploads = Math.max(0, this.maxPrecheckUploads - this.totalSubmissions);
    } else {
      this.remainingUploads = this.maxPrecheckUploads;
    }
  }
}

